import { z } from 'zod';
import { ReportFiltersSchema } from './api.js';
import { CriterionKeySchema, CriterionResultValueSchema, CriterionSourceSchema, ScoreDimensionSchema, SeveritySchema, SLA_BANDS, TOUCH_TIMINGS } from './evaluation.js';
import { ScoreCardSchema } from './scores.js';

// Advisor dashboard (spec §23). Cohort = conversations (episodes) that started in the period, like first-response
// reports. Durations use the evaluation policy clock (business hours by default). Null = no data, never 0.

const Iso = z.string();
const Rate = z.number().min(0).max(1).nullable();
const Seconds = z.number().nullable();

const BandCountsSchema = z.object(Object.fromEntries(SLA_BANDS.map((b) => [b, z.number().int()])) as Record<(typeof SLA_BANDS)[number], z.ZodNumber>);

export const DurationStatsSchema = z.object({
  /** Measured observations (completed, reliable). */
  count: z.number().int(),
  medianSeconds: Seconds,
  meanSeconds: Seconds,
  p90Seconds: Seconds,
  maxSeconds: Seconds,
  /** Share of measured observations within SLA (excellent + acceptable). */
  withinSlaRate: Rate,
  /** Observations outside the SLA (slow + critical). */
  outsideSla: z.number().int(),
  bands: BandCountsSchema,
  /** Still waiting (not decidable yet) — excluded from the statistics. */
  pending: z.number().int(),
  /** Not measurable (coverage gaps, ambiguous ordering) — excluded from the statistics. */
  unknown: z.number().int(),
});
export type DurationStats = z.infer<typeof DurationStatsSchema>;

export const AdvisorMetricsSchema = z.object({
  conversations: z.number().int(),
  attention: z.object({
    /** Conversations started by the lead. */
    leadsReceived: z.number().int(),
    /** Conversations started by the advisor (outreach). */
    advisorInitiated: z.number().int(),
    /** Lead-started conversations that got a first contact. */
    contacted: z.number().int(),
    contactedRate: Rate,
    /** Lead-started conversations still without any contact at computation time. */
    uncontacted: z.number().int(),
    firstContact: DurationStatsSchema,
  }),
  conversation: z.object({
    replies: DurationStatsSchema,
    /** Open conversations. */
    active: z.number().int(),
    /** Prospect waiting for a reply (not yet abandoned). */
    waiting: z.number().int(),
    /** Conversations abandoned with the prospect's message unanswered. */
    abandoned: z.number().int(),
    stalled: z.number().int(),
    /** Median duration first → last message. */
    medianActiveSeconds: Seconds,
  }),
  followUp: z.object({
    /** Unanswered sequences that needed at least one follow-up. */
    sequencesNeedingFollowUp: z.number().int(),
    /** Σ follow-ups done / Σ follow-ups expected (cadence). */
    complianceRate: Rate,
    /** Sequences that needed follow-up and got none. */
    withoutFollowUp: z.number().int(),
    singleTouchAbandoned: z.number().int(),
    overdue: z.number().int(),
    /** Average advisor touches per conversation (attempts, bursts ≤ burst gap = 1 touch). */
    averageTouches: z.number().nullable(),
    cadence: z.object(Object.fromEntries(TOUCH_TIMINGS.map((t) => [t, z.number().int()])) as Record<(typeof TOUCH_TIMINGS)[number], z.ZodNumber>),
    /** Conversations where the prospect came back after a follow-up or reactivation. */
    recovered: z.number().int(),
    /** Touch number at which prospects answered after follow-ups ("conversión por número de toque"). */
    replyByTouch: z.array(z.object({ touch: z.number().int(), count: z.number().int() })),
  }),
  /** §23 Comercial — from Conversation Intelligence (AI). Appointments are inferred from the chat, never verified. */
  commercial: z.object({
    /** Conversations with a current AI evaluation. */
    evaluated: z.number().int(),
    qualified: z.number().int(),
    appointmentOpportunities: z.number().int(),
    appointmentsProposed: z.number().int(),
    appointmentsAccepted: z.number().int(),
    /** Confirmed in the conversation (AI-inferred, not verified). */
    appointmentsConfirmedInChat: z.number().int(),
    /** Confirmed by an external system of record (future CRM/calendar). */
    appointmentsVerified: z.number().int(),
    /** (accepted + confirmed) / conversations evaluated by AI (appointments only exist where AI evaluated). */
    appointmentsPerContactedLead: Rate,
    hotLeads: z.number().int(),
    hotLeadsWithoutAppointment: z.number().int(),
    questionsAsked: z.number().int(),
    questionsUnanswered: z.number().int(),
    objections: z.number().int(),
    objectionsWellHandled: z.number().int(),
    withoutNextStep: z.number().int(),
  }),
  communication: z.object({
    evaluated: z.number().int(),
    withDensityIssues: z.number().int(),
    /** Conversations where materials (brochure, video, ubicación, PDF, links) were sent. */
    withMaterials: z.number().int(),
  }),
});
export type AdvisorMetrics = z.infer<typeof AdvisorMetricsSchema>;

export const AdvisorReportQuerySchema = ReportFiltersSchema.extend({
  groupBy: z.enum(['agent', 'connection']).default('agent'),
});
export type AdvisorReportQuery = z.infer<typeof AdvisorReportQuerySchema>;

export const AdvisorRowSchema = z.object({
  key: z.string(),
  name: z.string(),
  kind: z.enum(['agent', 'connection', 'shared', 'unassigned']),
  metrics: AdvisorMetricsSchema,
  /** Six explained scores (§22). */
  scores: ScoreCardSchema,
});
export type AdvisorRow = z.infer<typeof AdvisorRowSchema>;

export const AdvisorReportSchema = z.object({
  filters: AdvisorReportQuerySchema,
  timezone: z.string(),
  computedAt: Iso,
  isDemo: z.boolean(),
  clock: z.enum(['business', 'elapsed']),
  policyVersion: z.number().int(),
  /** Conversations in the cohort whose criteria were computed with an older policy version (recalculating). */
  stalePolicyConversations: z.number().int(),
  summary: AdvisorMetricsSchema,
  summaryScores: ScoreCardSchema,
  /** Scores must be calibrated on manually reviewed conversations before comparing people. */
  calibration: z.object({ target: z.number().int(), reviewed: z.number().int(), ready: z.boolean() }),
  rows: z.array(AdvisorRowSchema),
});
export type AdvisorReport = z.infer<typeof AdvisorReportSchema>;

// ───────── Drill-down: criterion → conversations ─────────

export const CriterionDrilldownQuerySchema = ReportFiltersSchema.extend({
  criterionKey: CriterionKeySchema.optional(),
  dimension: ScoreDimensionSchema.optional(),
  /** Default: failed + partial (what needs attention). */
  result: z.enum(['attention', 'failed', 'partial', 'passed', 'pending', 'unknown', 'all']).default('attention'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type CriterionDrilldownQuery = z.infer<typeof CriterionDrilldownQuerySchema>;

export const CriterionDrilldownItemSchema = z.object({
  id: z.string(),
  criterionKey: CriterionKeySchema,
  source: CriterionSourceSchema,
  conversationId: z.string(),
  episodeId: z.string(),
  contactName: z.string(),
  connectionLabel: z.string(),
  agentName: z.string().nullable(),
  episodeStartAt: Iso,
  observedAt: Iso.nullable(),
  result: CriterionResultValueSchema,
  severity: SeveritySchema.nullable(),
  band: z.string().nullable(),
  explanation: z.string().nullable(),
  recommendedAction: z.string().nullable(),
  evidenceMessageIds: z.array(z.string()),
});
export type CriterionDrilldownItem = z.infer<typeof CriterionDrilldownItemSchema>;

export const CriterionDrilldownSchema = z.object({
  items: z.array(CriterionDrilldownItemSchema),
  total: z.number().int(),
  nextCursor: z.string().nullable(),
});
export type CriterionDrilldown = z.infer<typeof CriterionDrilldownSchema>;

